// @flow
import { combineReducers } from 'redux';
import { filter, get } from 'lodash';
import { AsyncRequest } from 'angieslist-app-platform';
import profileActionKeys from './profileActionKeys';
import { getProfileCompletionInfo, getServiceProviderProfile, getServiceProviderCurrentProfile } from './profileActions';
import advertiseUtils from '../../../app/common/advertise/advertiseUtils';
import type {
  ArchiveShowcaseRequest,
  GetProfileRequest,
  GetProfileResponse,
  ServiceProviderShowcaseListRequest,
  ServiceProviderShowcaseListResponse,
  GetAllBusinessHighlightsResponse,
  GetProfileCompletionResponse,
  GetProfileCompletionRequest,
} from './profileService';
import { getCoverPhoto } from '../../../app/common/images/imagesActions';
import type { AsyncState } from '../../../app/types';


export type GetProfileState =
  AsyncState<GetProfileRequest, GetProfileResponse>;

export type GetProfileUAPIState =
  AsyncState<any, any>;

export type SaveProfileState =
  AsyncState<any, any>;

export type GetShowcasesState =
  AsyncState<ServiceProviderShowcaseListRequest, ServiceProviderShowcaseListResponse>;

export type ArchiveShowcaseState =
  AsyncState<ArchiveShowcaseRequest, {}>;

export type ShowcaseAdvertiseState =
  AsyncState<{}, {}>;

export type AllBusinessHighlightsState =
  AsyncState<{}, GetAllBusinessHighlightsResponse>;

export type GetProfileCompletionState =
  AsyncState<GetProfileCompletionRequest, GetProfileCompletionResponse>;

export type ProfileCompletionReducerState = {
  getCompletionProfileState: GetProfileCompletionState,
}

export type ProfileReducerState = {
  currentProfile: GetProfileState,
  currentProfileUAPI: GetProfileUAPIState,
  saveProfile: SaveProfileState,
  getShowcases: GetShowcasesState,
  archiveShowcase: ArchiveShowcaseState,
  showcaseAdvertiseRequest: ShowcaseAdvertiseState,
  getAllBusinessHighlights: AllBusinessHighlightsState,
  getProfileCompletion: GetProfileCompletionState,
}

const genericSubmitError = 'There was an error saving your license information. Please refresh the page and try again.';

export default (combineReducers({
  currentProfile: AsyncRequest.createReducer(profileActionKeys.GET_PROFILE),
  currentProfileUAPI: AsyncRequest.createReducer(profileActionKeys.GET_PROFILE_UAPI, {
    [profileActionKeys.GET_PROFILE_UAPI_SUCCESS]: (state, action) => {
      action.addSideEffect((store) => {
        const storeState = store.getState();
        const serviceProviderId = get(storeState, 'serviceProviderId');

        store.dispatch(getProfileCompletionInfo({ serviceProviderId }));
      });
      return AsyncRequest.setSuccess(state, action.payload.body);
    },
    [profileActionKeys.DELETE_LICENSE_SUCCESS]: (state, action) => {
      const profileResponse = state.get('response').toJS();
      profileResponse.businessInfo.licenses = filter(
        profileResponse.businessInfo.licenses,
        license => license.licenseId !== action.meta.tradeLicenseInfoId,
      );
      return AsyncRequest.setSuccess(state, profileResponse);
    },
  }),
  saveProfile: AsyncRequest.createReducer(profileActionKeys.SAVE_PROFILE, {
    [profileActionKeys.SAVE_PROFILE_SUCCESS]: (state, action) => {
      action.addSideEffect((store) => {
        const storeState = store.getState();
        const serviceProviderId = get(storeState, 'serviceProviderId');

        store.dispatch(getServiceProviderCurrentProfile({ serviceProviderId }));
        store.dispatch(getProfileCompletionInfo({ serviceProviderId }));
      });
      return AsyncRequest.setSuccess(state, action.payload.body);
    },
  }),
  getCategoryAutocomplete: AsyncRequest.createReducer(profileActionKeys.GET_CATEGORY_AUTOCOMPLETE),
  saveBondedInsured: AsyncRequest.createReducer(profileActionKeys.SAVE_BONDED_INSURED),
  createLicense: AsyncRequest.createReducer(profileActionKeys.CREATE_LICENSE, {
    [profileActionKeys.CREATE_LICENSE_SUCCESS]: (state, action) => {
      action.addSideEffect((store) => {
        const storeState = store.getState();
        const serviceProviderId = get(storeState, 'serviceProviderId');
        const { uuid } = get(storeState.serviceProviderCache, serviceProviderId, {});

        store.dispatch(getProfileCompletionInfo({ serviceProviderId }));
        store.dispatch(getServiceProviderProfile({ uuid }));
      });
      return AsyncRequest.setSuccess(state, action.payload.body);
    },
    [profileActionKeys.CREATE_LICENSE_ERROR]: state =>
      AsyncRequest.setError(state, genericSubmitError),
  }),
  deleteLicense: AsyncRequest.createReducer(profileActionKeys.DELETE_LICENSE),
  updateLicense: AsyncRequest.createReducer(profileActionKeys.UPDATE_LICENSE, {
    [profileActionKeys.UPDATE_LICENSE_SUCCESS]: (state, action) => {
      action.addSideEffect((store) => {
        const storeState = store.getState();
        const serviceProviderId = get(storeState, 'serviceProviderId');
        const { uuid } = get(storeState.serviceProviderCache, serviceProviderId, {});

        store.dispatch(getProfileCompletionInfo({ serviceProviderId }));
        store.dispatch(getServiceProviderProfile({ uuid }));
      });
      return AsyncRequest.setSuccess(state, action.payload.body);
    },
    [profileActionKeys.UPDATE_LICENSE_ERROR]: state =>
      AsyncRequest.setError(state, genericSubmitError),
  }),
  updateLicenseAttestation:
    AsyncRequest.createReducer(profileActionKeys.UPDATE_LICENSE_ATTESTATION),
  getShowcases: AsyncRequest.createReducer(profileActionKeys.GET_SHOWCASES, {
    [profileActionKeys.ARCHIVE_SHOWCASE_SUCCESS]: (state, action) => {
      const showcaseResponse = state.get('response').toJS();
      action.addSideEffect((store) => {
        const storeState = store.getState();
        const serviceProviderId = get(storeState, 'serviceProviderId');
        store.dispatch(getProfileCompletionInfo({ serviceProviderId }));
      });
      return AsyncRequest.setSuccess(state, {
        showcaseCount: showcaseResponse.showcaseCount - 1,
        showcases: showcaseResponse.showcases.filter(s => s.showcaseId !== action.meta.showcaseId),
      });
    },
  }),
  thimbleUrl: AsyncRequest.createReducer(profileActionKeys.GET_THIMBLE_URL),
  archiveShowcase: AsyncRequest.createReducer(profileActionKeys.ARCHIVE_SHOWCASE),
  showcaseAdvertiseRequest:
    AsyncRequest.createReducer(profileActionKeys.SHOWCASE_ADVERTISE_REQUEST, {
      [profileActionKeys.SHOWCASE_ADVERTISE_REQUEST_SUCCESS]: (state, action) => {
        action.addSideEffect(store =>
          advertiseUtils.trackAdvertiseRequestSent(true, action.meta, store));
        return AsyncRequest.setSuccess(state, true);
      },
      [profileActionKeys.SHOWCASE_ADVERTISE_REQUEST_ERROR]: (state, action) => {
        action.addSideEffect(store =>
          advertiseUtils.trackAdvertiseRequestSent(false, action.meta, store));
        return AsyncRequest.setError(state, action.payload);
      },
    }),
  getAllBusinessHighlights:
    AsyncRequest.createReducer(profileActionKeys.GET_ALL_BUSINESS_HIGHLIGHTS),
  getProfileCompletion:
    AsyncRequest.createReducer(profileActionKeys.GET_PROFILE_COMPLETION),
  getUUIdBySpId: AsyncRequest.createReducer(profileActionKeys.GET_PROFILE_UUID, {
    [profileActionKeys.GET_PROFILE_UUID_SUCCESS]: (state, action) => {
      action.addSideEffect((store) => {
        const storeState = store.getState();
        const uuid = get(storeState, 'serviceProviderProfile.getUUIdBySpId.response');
        store.dispatch(getServiceProviderProfile({ uuid }));
        store.dispatch(getCoverPhoto(uuid));
      });
      return AsyncRequest.setSuccess(state, action.payload.body);
    },
  }),
}): any);



// WEBPACK FOOTER //
// ./src/modules/profile/common/profileReducer.js